import React, { useEffect, useState } from "react";
import { BrainCircuit, Telescope, Check, X, Loader2, RefreshCw, Sparkles, FlaskConical, ShieldCheck, Mic, Waves, Ear } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const VERDICT = {
  ADOPT: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
  REINFORCE: "text-cyan-300 bg-cyan-400/10 border-cyan-400/30",
  WATCH: "text-amber-300 bg-amber-400/10 border-amber-400/30",
  REJECT: "text-slate-400 bg-white/5 border-white/10",
};
const MODEL_TINT = {
  "GPT-5.6 Terra": "text-emerald-300", "GPT-5.6 Sol": "text-emerald-300",
  "Gemini 3.1 Pro": "text-sky-300", "Claude Opus 4.8": "text-orange-300", "Claude Haiku 4.5": "text-orange-200",
};

function Chip({ children, className = "" }) {
  return <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono-data uppercase tracking-wider border ${className}`}>{children}</span>;
}

export default function PrefrontalCortex() {
  const [data, setData] = useState(null);
  const [upgrades, setUpgrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [q, setQ] = useState("");
  const [reflecting, setReflecting] = useState(false);
  const [reflection, setReflection] = useState(null);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/brain/prefrontal"),
        api.get("/brain/prefrontal/upgrades", { params: { limit: 40 } }),
      ]);
      setData(s.data);
      setUpgrades(u.data.upgrades || []);
    } catch {
      toast.error("Could not reach the Prefrontal Cortex.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setScanning(true);
    try {
      await api.post("/brain/prefrontal/scan");
      toast.success("Frontier scan running — proposals arrive in ~1–2 min. Auto-refreshing.");
      setTimeout(load, 60000);
      setTimeout(load, 100000);
    } catch { toast.error("Scan failed to start."); }
    finally { setTimeout(() => setScanning(false), 3000); }
  };

  const act = async (uid, action) => {
    setBusyId(uid);
    try {
      const res = await api.post(`/brain/prefrontal/upgrades/${uid}/${action}`);
      if (action === "approve" && res.data?.ok) toast.success("Adopted into AVA's Cognitive Doctrine — now live fleet-wide.");
      else if (action === "approve") toast.warning(res.data?.message || "Nothing to adopt.");
      else toast.message("Proposal dismissed.");
      await load();
    } catch { toast.error("Action failed."); }
    finally { setBusyId(null); }
  };

  const reflect = async () => {
    if (!q.trim()) return;
    setReflecting(true); setReflection(null);
    try {
      const res = await api.post("/brain/prefrontal/reflect", { query: q.trim() });
      setReflection(res.data);
    } catch { toast.error("Reflection failed."); }
    finally { setReflecting(false); }
  };

  const pending = upgrades.filter((u) => u.status === "pending");
  const history = upgrades.filter((u) => u.status !== "pending");

  return (
    <div className="col-span-1 md:col-span-10 h-full glass-panel rounded-2xl p-4 sm:p-6 overflow-y-auto" data-testid="prefrontal-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0">
            <BrainCircuit className="w-5 h-5 text-cyan-300" />
          </div>
          <div>
            <h2 className="font-display font-bold text-white text-lg leading-tight">Prefrontal Cortex</h2>
            <p className="text-slate-400 text-xs mt-1 max-w-2xl">
              AVA's executive faculty. It audits the <span className="text-cyan-300">global AI + neuroscience frontier</span> — OpenAI, Anthropic, Google, DeepSeek, Qwen, Kimi, GLM — and proposes reasoning upgrades that <span className="text-cyan-300">reinforce</span> the HNHB brain. Nothing changes how AVA thinks until you approve it.
            </p>
          </div>
        </div>
        <button
          data-testid="prefrontal-scan-button"
          onClick={runScan}
          disabled={scanning}
          className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 border border-cyan-400/30 text-[11px] font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
        >
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Telescope className="w-3.5 h-3.5" />}
          {scanning ? "Scanning…" : "Run Frontier Scan"}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Reading cognitive state…</div>
      ) : (
        <>
          {/* Routing + stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6" data-testid="prefrontal-stats">
            <div className="glass-panel rounded-xl p-3 border border-white/5">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500">Active Doctrine</div>
              <div className="text-2xl font-display font-bold text-cyan-300">{data?.doctrine_count ?? 0}</div>
            </div>
            <div className="glass-panel rounded-xl p-3 border border-white/5">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500">Pending Approval</div>
              <div className="text-2xl font-display font-bold text-amber-300">{data?.pending_count ?? 0}</div>
            </div>
            <div className="glass-panel rounded-xl p-3 border border-white/5 col-span-2">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 mb-1.5">Reasoning Engines (best model per task)</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(data?.routing || {}).map(([task, model]) => (
                  <Chip key={task} className={`border-white/10 ${MODEL_TINT[model] || "text-slate-300"}`}>{task.replace(/_/g, " ")}: {model}</Chip>
                ))}
              </div>
            </div>
          </div>

          {/* Pending upgrades — one-tap approval */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-300" />
              <h3 className="font-display font-semibold text-white text-sm">Proposed Cognitive Upgrades</h3>
              <span className="text-[10px] font-mono-data text-slate-500">awaiting your approval</span>
            </div>
            {pending.length === 0 ? (
              <p className="text-slate-500 text-xs italic" data-testid="prefrontal-no-pending">No pending proposals. Run a frontier scan to have AVA audit the latest research.</p>
            ) : (
              <div className="space-y-3" data-testid="prefrontal-pending-list">
                {pending.map((u) => (
                  <div key={u.uid} data-testid={`prefrontal-upgrade-${u.uid}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Chip className={VERDICT[u.verdict] || VERDICT.WATCH}>{u.verdict}</Chip>
                        <span className="text-white text-sm font-display font-semibold">{u.method || "Cognitive upgrade"}</span>
                        {u.model && <Chip className={`border-white/10 ${MODEL_TINT[u.model] || "text-slate-400"}`}>{u.model}</Chip>}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`approve-upgrade-${u.uid}`}
                          onClick={() => act(u.uid, "approve")}
                          disabled={busyId === u.uid}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25 border border-emerald-400/30 text-[10px] font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
                        >
                          {busyId === u.uid ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                        </button>
                        <button
                          data-testid={`reject-upgrade-${u.uid}`}
                          onClick={() => act(u.uid, "reject")}
                          disabled={busyId === u.uid}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 text-slate-400 hover:bg-white/10 border border-white/10 text-[10px] font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
                        >
                          <X className="w-3 h-3" /> Dismiss
                        </button>
                      </div>
                    </div>
                    {u.directive && (
                      <p className="text-cyan-200 text-xs mb-1.5"><span className="text-slate-500 font-mono-data uppercase text-[9px] mr-1">Directive</span>{u.directive}</p>
                    )}
                    <p className="text-slate-400 text-xs leading-relaxed">{u.mechanism}</p>
                    {u.apply && <p className="text-slate-500 text-[11px] mt-1.5 italic">Applies as: {u.apply}</p>}
                    {u.theme && <p className="text-slate-600 text-[10px] mt-2 font-mono-data">frontier theme: {u.theme}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active doctrine */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-cyan-300" />
              <h3 className="font-display font-semibold text-white text-sm">Live Cognitive Doctrine</h3>
              <span className="text-[10px] font-mono-data text-slate-500">flows to every ship</span>
            </div>
            {(data?.doctrine || []).length === 0 ? (
              <p className="text-slate-500 text-xs italic">No approved directives yet. Approve an upgrade above to engrain it into AVA's reasoning.</p>
            ) : (
              <ul className="space-y-2" data-testid="prefrontal-doctrine-list">
                {data.doctrine.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-300 rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
                    <span className="text-cyan-400 mt-0.5">◆</span>
                    <div>
                      <span>{d.directive}</span>
                      <span className="text-slate-600 text-[10px] ml-2 font-mono-data">{d.method}{d.model ? ` · ${d.model}` : ""}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Deep reflection tool */}
          <div className="mb-2">
            <div className="flex items-center gap-2 mb-3">
              <FlaskConical className="w-4 h-4 text-purple-300" />
              <h3 className="font-display font-semibold text-white text-sm">Deliberate Self-Reflection</h3>
              <span className="text-[10px] font-mono-data text-slate-500">GPT-5.6 drafts → Gemini critiques → Claude revises</span>
            </div>
            <div className="flex gap-2 mb-3">
              <input
                data-testid="prefrontal-reflect-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && reflect()}
                placeholder="Pose a hard question and watch AVA think about its own thinking…"
                className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-400/40"
              />
              <button
                data-testid="prefrontal-reflect-button"
                onClick={reflect}
                disabled={reflecting || !q.trim()}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-400/15 text-purple-200 hover:bg-purple-400/25 border border-purple-400/30 text-[11px] font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
              >
                {reflecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />}
                {reflecting ? "Reflecting…" : "Reflect"}
              </button>
            </div>
            {reflection?.ok && (
              <div className="rounded-xl border border-purple-400/20 bg-purple-400/[0.04] p-4" data-testid="prefrontal-reflection-result">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Chip className="text-purple-200 border-purple-400/30 bg-purple-400/10">Confidence {reflection.confidence != null ? `${Math.round(reflection.confidence * 100)}%` : "—"}</Chip>
                  {Object.entries(reflection.models || {}).map(([k, v]) => (
                    <Chip key={k} className={`border-white/10 ${MODEL_TINT[v] || "text-slate-400"}`}>{k}: {v}</Chip>
                  ))}
                </div>
                <p className="text-slate-200 text-xs whitespace-pre-wrap leading-relaxed">{reflection.answer}</p>
                <details className="mt-2">
                  <summary className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 cursor-pointer">Self-critique trace</summary>
                  <p className="text-slate-500 text-[11px] whitespace-pre-wrap mt-1.5 leading-relaxed">{reflection.critique}</p>
                </details>
              </div>
            )}
          </div>

          {/* Voice Learning — self-improving voice experience */}
          <VoiceLearningSection />

          {/* History */}
          {history.length > 0 && (
            <details className="mt-4">
              <summary className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 cursor-pointer flex items-center gap-1.5">
                <RefreshCw className="w-3 h-3" /> Reviewed proposals ({history.length})
              </summary>
              <div className="mt-2 space-y-1.5">
                {history.map((u) => (
                  <div key={u.uid} className="flex items-center gap-2 text-[11px] text-slate-500 py-1">
                    <Chip className={u.status === "approved" ? VERDICT.ADOPT : VERDICT.REJECT}>{u.status}</Chip>
                    <span className="text-slate-400">{u.method}</span>
                    {u.model && <span className={`text-[9px] ${MODEL_TINT[u.model] || ""}`}>{u.model}</span>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function VoiceLearningSection() {
  const [vl, setVl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try { const r = await api.get("/brain/voice-learning"); setVl(r.data); }
    catch { /* silent — folded into Cortex */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/brain/voice-learning/scan");
      if (r.data?.skipped) toast.message(r.data?.reason || "No new voice activity to learn from yet.");
      else toast.success(`Learned from your voice — ${(r.data?.auto_applied || []).length} refinement(s) applied, ${(r.data?.proposed || []).length} awaiting your approval.`);
      await load();
    } catch { toast.error("Voice review failed."); }
    finally { setScanning(false); }
  };

  const act = async (uid, action) => {
    setBusyId(uid);
    try {
      await api.post(`/brain/voice-learning/proposals/${uid}/${action}`);
      if (action === "approve") toast.success("Approved — now live in AVA's voice fleet-wide.");
      else toast.message("Refinement dismissed.");
      await load();
    } catch { toast.error("Action failed."); }
    finally { setBusyId(null); }
  };

  const review = vl?.last_review;
  const pending = vl?.pending || [];
  const doctrine = vl?.doctrine || [];
  const tacit = vl?.tacit_recent || [];

  return (
    <div className="mb-6 mt-8 pt-6 border-t border-white/10" data-testid="voice-learning-section">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Waves className="w-4 h-4 text-teal-300" />
          <div>
            <h3 className="font-display font-semibold text-white text-sm">Voice Learning</h3>
            <p className="text-slate-500 text-[11px] mt-0.5 max-w-xl">
              AVA reviews her own live voice conversations — what worked, what didn't — and rewrites her <span className="text-teal-300">Voice Doctrine</span>. Small tweaks auto-apply; big changes wait for you. She also absorbs what you reveal out loud.
            </p>
          </div>
        </div>
        <button
          data-testid="voice-learning-scan-button"
          onClick={scan}
          disabled={scanning}
          className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-400/15 text-teal-200 hover:bg-teal-400/25 border border-teal-400/30 text-[11px] font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
        >
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ear className="w-3.5 h-3.5" />}
          {scanning ? "Reviewing…" : "Learn From My Voice Now"}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center h-16 text-slate-500 text-xs"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Reading voice logs…</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="glass-panel rounded-xl p-3 border border-white/5">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500">Voice Doctrine</div>
              <div className="text-2xl font-display font-bold text-teal-300">{vl?.doctrine_count ?? 0}</div>
            </div>
            <div className="glass-panel rounded-xl p-3 border border-white/5">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500">Awaiting You</div>
              <div className="text-2xl font-display font-bold text-amber-300">{vl?.pending_count ?? 0}</div>
            </div>
            <div className="glass-panel rounded-xl p-3 border border-white/5">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500">Events Logged</div>
              <div className="text-2xl font-display font-bold text-slate-300">{vl?.events_total ?? 0}</div>
            </div>
          </div>

          {review && (review.what_worked?.length || review.what_didnt?.length) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className="rounded-xl border border-emerald-400/15 bg-emerald-400/[0.03] p-3" data-testid="voice-what-worked">
                <div className="text-[10px] font-mono-data uppercase tracking-wider text-emerald-300/80 mb-1.5">What worked</div>
                <ul className="space-y-1">
                  {(review.what_worked || []).map((w, i) => <li key={i} className="text-slate-300 text-[11px] leading-snug">• {w}</li>)}
                  {(review.what_worked || []).length === 0 && <li className="text-slate-600 text-[11px] italic">—</li>}
                </ul>
              </div>
              <div className="rounded-xl border border-rose-400/15 bg-rose-400/[0.03] p-3" data-testid="voice-what-didnt">
                <div className="text-[10px] font-mono-data uppercase tracking-wider text-rose-300/80 mb-1.5">What didn't</div>
                <ul className="space-y-1">
                  {(review.what_didnt || []).map((w, i) => <li key={i} className="text-slate-300 text-[11px] leading-snug">• {w}</li>)}
                  {(review.what_didnt || []).length === 0 && <li className="text-slate-600 text-[11px] italic">—</li>}
                </ul>
              </div>
            </div>
          ) : null}

          {pending.length > 0 && (
            <div className="mb-4" data-testid="voice-learning-pending-list">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-amber-300/80 mb-2">Big changes awaiting your approval</div>
              <div className="space-y-2">
                {pending.map((p) => (
                  <div key={p.uid} data-testid={`voice-proposal-${p.uid}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <Chip className="text-amber-300 bg-amber-400/10 border-amber-400/30">{p.category}</Chip>
                      <div className="flex items-center gap-2">
                        <button data-testid={`voice-approve-${p.uid}`} onClick={() => act(p.uid, "approve")} disabled={busyId === p.uid}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25 border border-emerald-400/30 text-[10px] font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50">
                          {busyId === p.uid ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                        </button>
                        <button data-testid={`voice-reject-${p.uid}`} onClick={() => act(p.uid, "reject")} disabled={busyId === p.uid}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 text-slate-400 hover:bg-white/10 border border-white/10 text-[10px] font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50">
                          <X className="w-3 h-3" /> Dismiss
                        </button>
                      </div>
                    </div>
                    <p className="text-teal-200 text-xs mt-2">{p.directive}</p>
                    {p.rationale && <p className="text-slate-500 text-[11px] mt-1 leading-relaxed">{p.rationale}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Mic className="w-3.5 h-3.5 text-teal-300" />
              <span className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500">Live Voice Doctrine — flows to every session</span>
            </div>
            {doctrine.length === 0 ? (
              <p className="text-slate-500 text-xs italic">Nothing learned yet. Tap “Learn From My Voice Now” after a few live conversations.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="voice-learning-doctrine-list">
                {doctrine.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-300 rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
                    <span className="text-teal-400 mt-0.5">◆</span>
                    <div>
                      <span>{d.directive}</span>
                      <span className="text-slate-600 text-[10px] ml-2 font-mono-data">{d.category}{d.auto_applied ? " · auto" : " · approved"}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {tacit.length > 0 && (
            <details data-testid="voice-tacit-list">
              <summary className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 cursor-pointer">Tacit knowledge she absorbed from your voice ({tacit.length})</summary>
              <ul className="mt-2 space-y-1">
                {tacit.map((t, i) => <li key={i} className="text-slate-400 text-[11px] leading-snug">• {t.text || t.title}</li>)}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}
